import LayOut from '../components/common/Layout';
import { Link } from 'react-router-dom';
import { Routes } from '../utils/Routes';
import { useEffect, useState } from 'react';
import FeaturedArt from '../components/common/FeaturedArt';

// Dynamically import all images from Art folder and subfolders
const importAll = (r: any) =>
  r.keys().map((key: string) => ({
    src: r(key),
    folder: key.split('/')[1],
    key,
  }));
const allImages = importAll(
  require.context('../assets/images/Art', true, /\.(jpg|jpeg|png|svg)$/),
);

// Group by folder and pick the first image per folder
const folderMap = new Map<
  string,
  { src: string; folder: string; key: string }
>();
allImages.forEach((img: any) => {
  if (!folderMap.has(img.folder)) {
    folderMap.set(img.folder, img);
  }
});
const artImages = Array.from(folderMap.values()).map((img) => img.src);

const navLinks = [
  { label: 'Art', to: Routes.Art },
  { label: 'Thoughts', to: Routes.Thoughts },
  { label: 'Music', to: Routes.Music },
  { label: 'Things', to: Routes.Things },
];

export default function Home() {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [nextImageIndex, setNextImageIndex] = useState(1);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [fade, setFade] = useState(false);

  useEffect(() => {
    const interval = setInterval(() => {
      const nextIdx = (currentImageIndex + 1) % artImages.length;
      setNextImageIndex(nextIdx);
      setIsTransitioning(true);
      setFade(false);
      setTimeout(() => {
        setCurrentImageIndex(nextIdx);
        setIsTransitioning(false);
        setFade(false);
      }, 600);
    }, 7000);
    return () => clearInterval(interval);
  }, [currentImageIndex]);

  useEffect(() => {
    if (isTransitioning) {
      const timeout = setTimeout(() => setFade(true), 10);
      return () => clearTimeout(timeout);
    }
  }, [isTransitioning]);

  return (
    <LayOut>
      <div className="relative w-full min-h-screen flex flex-col">
        {/* Background Images */}
        <div
          className="absolute inset-0 w-full h-full bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `url(${artImages[currentImageIndex]})`,
            filter: 'blur(4px)',
            zIndex: 0,
          }}
          aria-hidden="true"
        />
        {isTransitioning && (
          <div
            className={`absolute inset-0 w-full h-full bg-cover bg-center bg-no-repeat transition-opacity duration-600 ${fade ? 'opacity-100' : 'opacity-0'}`}
            style={{
              backgroundImage: `url(${artImages[nextImageIndex]})`,
              filter: 'blur(4px)',
              zIndex: 1,
            }}
            aria-hidden="true"
          />
        )}
        {/* Dark overlay */}
        <div
          className="absolute inset-0 w-full h-full backdrop-blur-sm bg-black/55"
          style={{ zIndex: 2 }}
          aria-hidden="true"
        />

        {/* Content */}
        <div
          className="relative flex flex-col items-center justify-center gap-12 py-24 px-4 w-full min-h-screen"
          style={{ zIndex: 3 }}
        >
          {/* Hero */}
          <section className="flex flex-col items-center text-center gap-5 w-full max-w-2xl mx-auto">
            <p className="text-xs uppercase tracking-widest text-custom-stone/80">
              Art · Thoughts · Music · Things
            </p>
            <h1 className="text-4xl md:text-6xl font-bold text-white tracking-tight drop-shadow-lg">
              Unspoken Creations
            </h1>
            <p className="text-sm md:text-base text-white/50 max-w-md leading-relaxed">
              House of Noticed Things
            </p>

            {/* Nav Links */}
            <div className="flex flex-wrap justify-center gap-3 mt-4">
              {navLinks.map(({ label, to }) => (
                <Link
                  key={label}
                  to={to}
                  className="px-6 py-2 rounded-full border border-white/20 text-white/70 text-sm font-medium tracking-wide hover:border-custom-stone hover:text-custom-stone transition-colors duration-200"
                >
                  {label}
                </Link>
              ))}
            </div>
          </section>

          {/* Featured Section */}
          <section className="w-full max-w-4xl mx-auto">
            <FeaturedArt />
          </section>
        </div>
      </div>
    </LayOut>
  );
}
